import * as React from 'react';
import { atom, useAtom, useAtomValue } from 'jotai';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { CheckCircleIcon, FaceFrownIcon } from '@heroicons/react/24/outline';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import { emailValidationRules, formatTimeLabel } from '@restworld/utils-common';
import { FormLabelWrapper, HelperTextRElement, TextInput } from '@restworld/ui-ds-base';

import { ModalToggleWrapper } from './form-modal-wrapper';
import { useEmployerEmailUpdateMutation } from './use-employer-user-information';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IUpdateUserEmailModalProps {}

export const emailVerificationModalVisibleAtom = atom<boolean>(false);
export const emailSentRemainingCountAtom = atom(3);

export const emailSentFlagAtom = atomWithStorage(
  'emailSentFlag',
  false,
  createJSONStorage<boolean>(() => localStorage)
);
export const emailSentAtom = atomWithStorage(
  'emailSent',
  '',
  createJSONStorage<string>(() => localStorage)
);
export const emailSentDateAtom = atomWithStorage(
  'emailSentDate',
  null,
  createJSONStorage<number>(() => localStorage)
);

const useEmailValidationRules = () => {
  const { t } = useTranslation('user-settings');
  return emailValidationRules({ message: t('must_be_a_valid_email') });
};

const UpdateUserEmailModal: React.FunctionComponent<IUpdateUserEmailModalProps> = () => {
  const [emailVerificationModalVisible, emailVerificationModalVisibleToggler] = useAtom(
    emailVerificationModalVisibleAtom
  );
  const [remainingTimes, setRemainingTimes] = useAtom(emailSentRemainingCountAtom);

  const methods = useForm<{ email?: string }>({
    defaultValues: { email: '' },
  });
  const [isEmailSent] = useAtom(emailSentFlagAtom);
  const [emailSent] = useAtom(emailSentAtom);
  const { mutate, isPending, isError, error, reset: resetMutation } = useEmployerEmailUpdateMutation();
  const { t } = useTranslation('user-settings');

  const {
    handleSubmit,
    formState: { errors },
    setError,
    reset,
  } = methods;
  const emailValidationRules = useEmailValidationRules();

  const { countDownLabel } = useCountDownTimerForEmailVerification();

  const onSubmit: SubmitHandler<{ email: string }> = React.useCallback(
    async (data) => {
      mutate(data);
      setRemainingTimes((prev) => prev - 1);
    },
    [mutate, setRemainingTimes]
  );

  const handleReset = React.useCallback(() => {
    emailVerificationModalVisibleToggler(false);
    setTimeout(() => {
      reset({ email: '' });
      resetMutation();
    }, 200);
  }, [emailVerificationModalVisibleToggler, reset, resetMutation]);

  const helperText = errors['email']?.message;
  const submitHandlerProps = React.useMemo(
    () =>
      isEmailSent
        ? { title: `Close ${countDownLabel ? `(${countDownLabel})` : ``}`, onClick: handleReset }
        : {
            title:
              remainingTimes === 0
                ? t('reached_max_attempts')
                : isError
                ? t('resend_verification_link')
                : t('send_verification_link'),
            disabled: remainingTimes === 0,
            loading: isPending,
            onClick: handleSubmit(onSubmit),
          },
    [isEmailSent, countDownLabel, handleReset, remainingTimes, t, isError, isPending, handleSubmit, onSubmit]
  );
  const modalProps = React.useMemo(() => {
    const title = isEmailSent
      ? `${t('sent_verification_link_to')} ${emailSent}`
      : isError
      ? t('something_went_wrong')
      : t('update_email_address');

    const subTitle = isEmailSent
      ? t('click_the_email_link')
      : isError
      ? t('click_the_email_link')
      : t('will_send_a_verification_link');

    const headerIcon = isEmailSent ? (
      <CheckCircleIcon className="w-6 h-6 text-neutral-700" />
    ) : isError ? (
      <FaceFrownIcon className="w-6 h-6 text-neutral-700" />
    ) : null;
    return {
      modalVisible: emailVerificationModalVisible,
      onModalClose: handleReset,
      title,
      subTitle,
      onCancelHandler: !isEmailSent ? handleReset : null,
      submitHandlerProps,
      headerIcon,
    };
  }, [t, emailSent, emailVerificationModalVisible, handleReset, isEmailSent, isError, submitHandlerProps]);

  React.useEffect(() => {
    if (isError) setError('email', { message: error?.['data']?.['error'] || t('something_went_wrong') });
  }, [error, isError, setError, t]);

  return (
    <ModalToggleWrapper {...modalProps}>
      {!isEmailSent && (
        <FormLabelWrapper isFullWidth label="Email" id="email">
          <Controller
            name="email"
            control={methods.control}
            rules={emailValidationRules}
            render={({ field }) => (
              <TextInput
                helperText={<HelperTextRElement helperText={helperText} />}
                color={helperText ? 'failure' : undefined}
                id="email"
                type="email"
                placeholder={t('insert_the_new_email')}
                data-cy={`update-employer-email-input`}
                autoFocus
                disabled={remainingTimes === 0}
                {...field}
              />
            )}
          />
        </FormLabelWrapper>
      )}
    </ModalToggleWrapper>
  );
};

export default UpdateUserEmailModal;
export const useCountDownTimerForEmailVerification = () => {
  const countDownForEmailResend = 5 * 60; // 5 minutes
  const [countDown, setCountDown] = React.useState(countDownForEmailResend);
  const [, setEmailSentFlag] = useAtom(emailSentFlagAtom);
  const [, setEmailSent] = useAtom(emailSentAtom);
  const [emailSentDate, setEmailSentDate] = useAtom(emailSentDateAtom);

  const resetEmailVerification = React.useCallback(() => {
    setEmailSentFlag(false);
    setEmailSent('');
    setEmailSentDate(null);
    setCountDown(countDownForEmailResend);
  }, [countDownForEmailResend, setEmailSent, setEmailSentDate, setEmailSentFlag]);

  // React.useEffect(() => {
  //   if (emailSentDate && !emailVerificationModalVisible) {
  //     const emailUpdateModalCountDiff = emailSentDate ? (Date.now() - emailSentDate) / 1000 : null;
  //     if (emailUpdateModalCountDiff && emailUpdateModalCountDiff > countDownForEmailResend) resetEmailVerification();
  //   }
  // }, [countDownForEmailResend, emailSentDate, emailVerificationModalVisible, resetEmailVerification]);

  React.useEffect(() => {
    if (!emailSentDate) return;
    const interval = setInterval(() => {
      setCountDown((prev) => {
        if (prev) return prev - 1;
        else {
          clearInterval(interval);
          resetEmailVerification();
          return 0;
        }
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [emailSentDate, resetEmailVerification]);

  return {
    countDown,
    countDownLabel: countDown > 0 ? formatTimeLabel(countDown) : null,
    resetTimer: () => setCountDown(countDownForEmailResend),
  };
};
